import { filter } from "@suse-o11y/filter";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isPresent } from "@suse-o11y/is-present";
import { map } from "@suse-o11y/map";
import { partition } from "@suse-o11y/partition";
import { pipe } from "@suse-o11y/pipe";
import { prop } from "@suse-o11y/prop";
import { Template } from "@suse-o11y/template";
import { TemplateVariablesBoundary } from "@suse-o11y/template-variables/boundary";
import { TemplateVariableDefinitions } from "@suse-o11y/template-variables/definitions";
import { Tuple } from "@suse-o11y/tuple";
import { useEffect } from "@suse-o11y/use-effect";
import { useMemo } from "@suse-o11y/use-memo";
import React, { useState } from "react";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import type { DashboardVariable } from "../../../common/dashboard-spec";
import { useQueries } from "../../../queries/react-query";

import { isAsyncReadVariable } from "./_derived-types/async-read-variable";
import { multipleValuesToString, queryDynamicVariableOptions } from "./_dynamic-variable-query";
import { interpolate } from "./_interpolate";
import { generateVariableNames } from "./generate-variable-names";

const DashboardTemplateVariablesBoundary = ({
	variables,
	timeRange,
	children,
}: {
	variables: ReadonlyArray<DashboardVariable>;
	timeRange: TimeRange;
	children: React.ReactNode;
}) => {
	const [definitions, setDefinitions] = useState(() => new TemplateVariableDefinitions([]));
	const [asyncReadVariables, syncReadVariables] = useMemo(() => partition(variables, isAsyncReadVariable), [variables]);

	const asyncReadVariableQueryDefinitions = useMemo(
		() =>
			map(asyncReadVariables, (variable) => ({
				...queryDynamicVariableOptions(variable, timeRange, (template) =>
					interpolate(definitions, variable.name, template),
				),
				refetchOnMount: false,
				refetchOnWindowFocus: false,
				keepPreviousData: true,
			})),
		[asyncReadVariables, definitions, timeRange],
	);
	const asyncReadVariableQueries = useQueries(asyncReadVariableQueryDefinitions);

	const asyncReadVariableDefinitions = useMemo(
		() =>
			pipe(
				asyncReadVariableQueries,
				map(prop("data")),
				filter(isPresent),
				map(([variable, values]) => Tuple(variable.name, multipleValuesToString(values))),
			),
		[asyncReadVariableQueries],
		isDeepEqual,
	);

	const syncReadVariableDefinitions = useMemo(
		() =>
			map(syncReadVariables, (variable) =>
				Tuple(
					variable.name,
					interpolate(definitions, variable.name, Template(extractSyncReadVariableValue(variable))) ?? null,
				),
			),
		[syncReadVariables, definitions],
	);

	useEffect(
		() => {
			const nextDefinitions = new TemplateVariableDefinitions(
				[...syncReadVariableDefinitions, ...asyncReadVariableDefinitions].flatMap(([varName, value]) =>
					map(generateVariableNames(varName), (name) => ({ name, value, detail: "variable" })),
				),
			);

			setDefinitions((current) => (current.isEqual(nextDefinitions) ? current : nextDefinitions));
		},
		[asyncReadVariableDefinitions, syncReadVariableDefinitions],
		isDeepEqual,
	);

	return <TemplateVariablesBoundary variables={definitions}>{children}</TemplateVariablesBoundary>;
};

function extractSyncReadVariableValue(variable: DashboardVariable): string {
	switch (variable.spec.kind) {
		case "TextVariable":
			return variable.spec.spec.value;

		case "ListVariable": {
			const { spec } = variable.spec;

			if (spec.defaultValue == null) {
				return "";
			}

			switch (spec.defaultValue.type) {
				case "Everything":
					if (spec.customAllValue != null) {
						return spec.customAllValue;
					}

					return spec.plugin.kind === "StaticList"
						? multipleValuesToString(spec.plugin.spec.values.map(prop("value")))
						: "";
				case "Value":
					return spec.defaultValue.value;
				case "Values":
					return multipleValuesToString(spec.defaultValue.values);
			}
		}
	}
}

export { DashboardTemplateVariablesBoundary };
